import { notifyError } from "@/lib/notify-error";
import { useRef, useState } from "react";
import { toast } from "sonner";
import type { Id } from "@constructionautomation/backend/convex/_generated/dataModel";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";

export interface DocumentTagSearchResult {
  _id: Id<"documentIntelligenceCanonicalTags">;
  rawValue: string;
  normalizedValue: string;
  detectionCount: number;
  revisionCount: number;
  /** Present when the adapter reports document/page coverage (e.g. FieldPilot plan set). */
  documentCount?: number;
  pageCount?: number;
  incompleteDocumentCount?: number;
  /** True when the tag is already present as a product task. */
  alreadyInJob?: boolean;
}

export interface DocumentTagSearchResponse {
  status: "too_short" | "available_in_job" | "matched" | "no_match";
  matches: DocumentTagSearchResult[];
}

export interface SearchDocumentTagsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSearchDocumentTags: (query: string) => Promise<DocumentTagSearchResponse>;
  onCreateCustomLineItemsFromDocumentTags: (
    documentTagIds: Id<"documentIntelligenceCanonicalTags">[]
  ) => Promise<void>;
  /** Optional domain-specific empty-state copy. Defaults preserve TakeoffPilot wording. */
  emptyStateMessages?: {
    beforeSearch?: string;
    tooShort?: string;
    availableInJob?: (query: string) => string;
    noMatch?: string;
  };
  description?: string;
}

const DEFAULT_EMPTY_STATE_MESSAGES = {
  beforeSearch: "Enter at least 2 characters to search.",
  tooShort: "Search for at least 2 characters.",
  availableInJob: (query: string) =>
    `${query} is already available in the tag list. Use the Tags dropdown to add it to results.`,
  noMatch: "No exact tag match found.",
} as const;

export function SearchDocumentTagsDialog({
  open,
  onOpenChange,
  onSearchDocumentTags,
  onCreateCustomLineItemsFromDocumentTags,
  emptyStateMessages,
  description = "Search exact normalized document tag matches and add selected tags as custom line items.",
}: SearchDocumentTagsDialogProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [lastSearchedQuery, setLastSearchedQuery] = useState("");
  const [searchResults, setSearchResults] = useState<DocumentTagSearchResult[]>([]);
  const [searchStatus, setSearchStatus] = useState<DocumentTagSearchResponse["status"] | null>(null);
  const [selectedSearchResults, setSelectedSearchResults] = useState<DocumentTagSearchResult[]>([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const searchRequestIdRef = useRef(0);

  const messages = {
    beforeSearch: emptyStateMessages?.beforeSearch ?? DEFAULT_EMPTY_STATE_MESSAGES.beforeSearch,
    tooShort: emptyStateMessages?.tooShort ?? DEFAULT_EMPTY_STATE_MESSAGES.tooShort,
    availableInJob:
      emptyStateMessages?.availableInJob ?? DEFAULT_EMPTY_STATE_MESSAGES.availableInJob,
    noMatch: emptyStateMessages?.noMatch ?? DEFAULT_EMPTY_STATE_MESSAGES.noMatch,
  };

  const resetState = () => {
    setSearchQuery("");
    setLastSearchedQuery("");
    setSearchResults([]);
    setSearchStatus(null);
    setSelectedSearchResults([]);
    setHasSearched(false);
  };

  const handleCancel = () => {
    searchRequestIdRef.current += 1;
    setIsSearching(false);
    resetState();
    onOpenChange(false);
  };

  const handleSearch = async () => {
    const trimmedQuery = searchQuery.trim();
    if (trimmedQuery.length < 2) {
      toast.error("Search for at least 2 characters");
      return;
    }

    setIsSearching(true);
    const requestId = searchRequestIdRef.current + 1;
    searchRequestIdRef.current = requestId;
    setLastSearchedQuery(trimmedQuery);
    try {
      const response = await onSearchDocumentTags(trimmedQuery);
      if (requestId !== searchRequestIdRef.current) return;
      setSearchResults(response.matches);
      setSearchStatus(response.status);
      setHasSearched(true);
    } catch (error) {
      if (requestId !== searchRequestIdRef.current) return;
      notifyError(error, "Failed to search tags");
      console.error(error);
    } finally {
      if (requestId === searchRequestIdRef.current) {
        setIsSearching(false);
      }
    }
  };

  const handleToggleSearchResult = (result: DocumentTagSearchResult, checked: boolean) => {
    setSelectedSearchResults((current) => {
      if (checked) {
        return current.some((selected) => selected._id === result._id) ? current : [...current, result];
      }
      return current.filter((selected) => selected._id !== result._id);
    });
  };

  const handleCreateSelectedSearchResults = async () => {
    if (selectedSearchResults.length === 0) {
      toast.error("Select at least one tag");
      return;
    }

    setIsSaving(true);
    try {
      await onCreateCustomLineItemsFromDocumentTags(
        selectedSearchResults.map((result) => result._id)
      );
      resetState();
      onOpenChange(false);
    } catch {
      // onCreateCustomLineItemsFromDocumentTags reports the failure.
    } finally {
      setIsSaving(false);
    }
  };

  const isBusy = isSaving || isSearching;

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        // Allow dismiss during read-only search; only block while a write is in flight.
        if (!isSaving) {
          if (!nextOpen) {
            searchRequestIdRef.current += 1;
            setIsSearching(false);
            resetState();
          }
          onOpenChange(nextOpen);
        }
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Search Document Tags</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="flex gap-2">
            <Input
              id="document-tag-search"
              aria-label="Search document tags"
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  void handleSearch();
                }
              }}
              placeholder="Search tags"
              disabled={isBusy}
            />
            <Button
              type="button"
              variant="outline"
              onClick={() => void handleSearch()}
              disabled={isBusy || searchQuery.trim().length < 2}
            >
              Search
            </Button>
          </div>
          <div className="min-h-12 rounded-md border border-border bg-muted/20 p-2" aria-live="polite">
            {!hasSearched ? (
              <p className="text-xs text-muted-foreground">{messages.beforeSearch}</p>
            ) : searchStatus === "too_short" ? (
              <p className="text-xs text-muted-foreground">{messages.tooShort}</p>
            ) : searchStatus === "available_in_job" ? (
              <p className="text-xs text-muted-foreground">
                {messages.availableInJob(lastSearchedQuery)}
              </p>
            ) : searchResults.length === 0 ? (
              <p className="text-xs text-muted-foreground">{messages.noMatch}</p>
            ) : (
              <div className="space-y-2">
                {searchResults.map((result) => {
                  const checked = selectedSearchResults.some((selected) => selected._id === result._id);
                  const coverageParts: string[] = [`${result.detectionCount} detections`];
                  if (result.documentCount !== undefined) {
                    coverageParts.push(
                      `${result.documentCount} drawing${result.documentCount === 1 ? "" : "s"}`
                    );
                  }
                  if (result.pageCount !== undefined) {
                    coverageParts.push(
                      `${result.pageCount} page${result.pageCount === 1 ? "" : "s"}`
                    );
                  }
                  if (
                    result.incompleteDocumentCount !== undefined &&
                    result.incompleteDocumentCount > 0
                  ) {
                    coverageParts.push(
                      `${result.incompleteDocumentCount} incomplete drawing${
                        result.incompleteDocumentCount === 1 ? "" : "s"
                      }`
                    );
                  }
                  return (
                    <label
                      key={result._id}
                      className="flex items-center gap-2 rounded-sm px-2 py-1 text-sm hover:bg-muted/60"
                    >
                      <Checkbox
                        checked={checked}
                        onCheckedChange={(nextChecked) =>
                          handleToggleSearchResult(result, nextChecked === true)
                        }
                        aria-label={`Select tag ${result.rawValue}`}
                      />
                      <span className="font-medium">{result.rawValue}</span>
                      <span className="text-xs text-muted-foreground">
                        {coverageParts.join(" · ")}
                        {result.alreadyInJob ? " · In job" : null}
                      </span>
                    </label>
                  );
                })}
              </div>
            )}
          </div>
          {selectedSearchResults.length > 0 ? (
            <div className="space-y-1">
              <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Selected tags
              </p>
              <div className="flex flex-wrap gap-1.5">
                {selectedSearchResults.map((result) => (
                  <span
                    key={result._id}
                    className="rounded-full border border-border bg-background px-2 py-1 text-xs font-medium"
                  >
                    {result.rawValue}
                  </span>
                ))}
              </div>
            </div>
          ) : null}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={handleCancel} disabled={isSaving}>
            Cancel
          </Button>
          <Button
            onClick={() => void handleCreateSelectedSearchResults()}
            disabled={isBusy || selectedSearchResults.length === 0}
          >
            OK
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
